import { useState } from "react"

// Local cn helper — no @/lib/utils alias dependency
const cn = (...classes) => classes.filter(Boolean).join(" ")

const compactNumber = (n) => {
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`
  return `${n}`
}

const RANGES = [
  { key: "24h", label: "24H" },
  { key: "3d", label: "3D" },
  { key: "1w", label: "1W" },
  { key: "1m", label: "1M" },
  { key: "1y", label: "1Y" },
]

const METRICS = {
  enrollments: { label: "Enrollments", color: "bg-sky-500" },
  completions: { label: "Completions", color: "bg-emerald-500" },
}

// Two clickable metric tabs (Enrollments / Completions) — tapping one switches
// which single series the bar chart shows, plus a range selector (24H–1Y).
// Bars use flex-1 so the chart always fills the available width and never
// scrolls, regardless of bucket count (24 hourly vs 12 monthly, etc).
export function EnrollmentCompletionChart({
  buckets = [],
  loading,
  range,
  onRangeChange,
  title = "Enrollment & Completion",
}) {
  const [metric, setMetric] = useState("enrollments")
  const [hoveredIndex, setHoveredIndex] = useState(null)

  const totalEnrollments = buckets.reduce((sum, b) => sum + (b.enrollments || 0), 0)
  const totalCompletions = buckets.reduce((sum, b) => sum + (b.completions || 0), 0)
  const conversionRate =
    totalEnrollments > 0 ? Math.round((totalCompletions / totalEnrollments) * 1000) / 10 : 0

  const maxValue = Math.max(1, ...buckets.map((b) => b[metric] || 0))
  const activeColor = METRICS[metric]
  const showLabels = buckets.length <= 12

  return (
    <div className="relative w-full p-5 rounded-2xl bg-foreground/[0.02] border border-foreground/[0.06] backdrop-blur-sm transition-all duration-500 hover:bg-foreground/[0.04] hover:border-foreground/[0.1] flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">{title}</span>
        <span className="text-[11px] text-muted-foreground">
          <span className="font-medium text-foreground">{conversionRate}%</span> conversion
        </span>
      </div>

      {/* Metric tabs — click to switch which series the chart shows */}
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(METRICS).map(([key, m]) => {
          const total = key === "enrollments" ? totalEnrollments : totalCompletions
          const isActive = metric === key
          return (
            <button
              key={key}
              onClick={() => setMetric(key)}
              className={cn(
                "text-left rounded-xl border px-3 py-2 transition-all duration-200",
                isActive ? "border-foreground/[0.12] bg-foreground/[0.04]" : "border-transparent hover:bg-foreground/[0.02]"
              )}
            >
              <div className="flex items-center gap-1.5">
                <span className={cn("w-1.5 h-1.5 rounded-full", m.color)} />
                <span className="text-[10px] text-muted-foreground">{m.label}</span>
              </div>
              <div
                className={cn(
                  "text-lg font-semibold tabular-nums mt-0.5",
                  isActive ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {compactNumber(total)}
              </div>
            </button>
          )
        })}
      </div>

            {/* Range selector */}
      <div className="hidden md:flex items-center gap-1">
        {RANGES.map((r) => (
          <button
            key={r.key}
            onClick={() => onRangeChange?.(r.key)}
            className={cn(
              "px-2 py-1 rounded-md text-[11px] font-medium transition-colors",
              range === r.key ? "bg-foreground/[0.08] text-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            {r.label}
          </button>
        ))}
      </div>

      <select
        value={range}
        onChange={(e) => onRangeChange?.(e.target.value)}
        aria-label="Select time range"
        className="md:hidden w-full text-[11px] font-medium rounded-md border border-foreground/[0.1] bg-transparent text-foreground px-2 py-1.5"
      >
        {RANGES.map((r) => (
          <option key={r.key} value={r.key}>
            {r.label}
          </option>
        ))}
      </select>

      {/* Chart — no horizontal scroll, bars share the row via flex-1 */}
      {loading ? (
        <div className="h-24 rounded-lg bg-muted animate-pulse" />
      ) : (
        <div className="flex items-end gap-1 h-24" onMouseLeave={() => setHoveredIndex(null)}>
          {buckets.map((b, index) => {
            const value = b[metric] || 0
            const height = Math.max((value / maxValue) * 88, value > 0 ? 3 : 0)
            const isHovered = hoveredIndex === index

            return (
              <div
                key={`${b.label}-${index}`}
                className="relative flex-1 min-w-0 flex flex-col items-center justify-end h-full"
                onMouseEnter={() => setHoveredIndex(index)}
              >
                <div
                  className={cn(
                    "absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 rounded-md bg-foreground text-background text-[11px] font-medium whitespace-nowrap transition-all duration-150 z-10",
                    isHovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1 pointer-events-none"
                  )}
                >
                  {value} {METRICS[metric].label.toLowerCase()}
                </div>
                <div
                  className={cn(
                    "w-full max-w-[10px] mx-auto rounded-t-sm transition-all duration-300 ease-out origin-bottom",
                    activeColor.color,
                    isHovered ? "opacity-100" : "opacity-60 hover:opacity-80"
                  )}
                  style={{ height: `${height}px` }}
                />
                {showLabels && (
                  <span
                    className={cn(
                      "text-[9px] mt-1 whitespace-nowrap",
                      isHovered ? "text-foreground" : "text-muted-foreground/60"
                    )}
                  >
                    {b.label}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default EnrollmentCompletionChart